import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import BizTextArea from '../../../Core/Components/Mini/BizTextArea';
import { BizAutoComplete } from '../../../Core/Components/Mini/BizAutoComplete';
import { BizSelect } from '../../../Core/Components/Mini/BizSelect';
import {
	useGetTimereports,
	useGetUserInfo,
	useGetActivities,
	useGetFavoriteActivities,
	useSetFavoriteActivity,
	useCreateTimereport,
	useUpdateTimereport,
	useSubmitAllTimereports,
	useGetTimereportTypes,
	//useUpdateFlag,
} from '../../../Core/Hooks/useTimereport';
import { TimereportActivity, TimereportItem } from '../../../Core/Types/admin/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import './TimereportDayView.scss';
import BizSplitButton from '../../../Core/Components/Mini/BizSplitButton';
import useWindowSize from '../../../Core/Hooks/useWindowSize';
import { useGlobalState } from '../../../Core/Contexts/globalStateContext';
import MonthStatsReport from './MonthStatsReport';
import { useTimereportSettings } from '../../../Core/Hooks/admin/useTimereportSettings';

const addDays = (date: Date, days: number): Date => {
	const d = new Date(date);
	d.setDate(d.getDate() + days);
	return d;
};

const getMonday = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1);
	d.setDate(diff);
	d.setHours(0, 0, 0, 0);
	return d;
};

const getLastDayOfMonth = (date: Date): Date => {
	return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

type RowState = {
	hours: number;
	comment: string;
};

type TimereportDayViewProps = {
	selectedDate?: string;
};

//const monthStatsTestDate: TimereportMonthStats[] = [
//	{ id: 1, dateFrom: '2024-06-01', dateTo: '2024-06-30', typeName: 'Development', hours: 140, userId: 1, portalId: 1, isDeleted: false, typeId: 1, hoursTime: 80, hoursMoney: 40, typeSystemName: 'NORMALTIME', typeIsSystemType: true },
//	{ id: 2, dateFrom: '2024-06-01', dateTo: '2024-06-30', typeName: 'Meeting', hours: 15, userId: 1, portalId: 1, isDeleted: false, typeId: 1, hoursTime: 10, hoursMoney: 0, typeSystemName: 'ABSENCE', typeIsSystemType: true },
//	{ id: 3, dateFrom: '2024-06-01', dateTo: '2024-06-30', typeName: 'Vacation', hours: 8, userId: 1, portalId: 1, isDeleted: false, typeId: 3, hoursTime: 8, hoursMoney: 0, typeSystemName: 'VACATION', typeIsSystemType: true },
//	{ id: 4, dateFrom: '2024-06-01', dateTo: '2024-06-30', typeName: 'Parental leave', hours: 8, userId: 1, portalId: 1, isDeleted: false, typeId: 3, hoursTime: 8, hoursMoney: 0, typeSystemName: 'PARENTALLEAVE', typeIsSystemType: true },
//];

// When true, all available activities are shown as rows (useful for customers with few activities).
// When false, only favorites and existing entries are shown (original behavior).
const SHOW_ALL_ACTIVITIES = true;

const TimereportDayView = ({ selectedDate }: TimereportDayViewProps) => {
	const { t, i18n } = useTranslation();
	const [currentDate, setCurrentDate] = useState(() => new Date());
	const locale = i18n.resolvedLanguage?.startsWith('sv') ? 'sv-SE' : 'en-GB';

	useEffect(() => {
		if (selectedDate) {
			setCurrentDate(new Date(selectedDate + 'T00:00:00'));
		}
	}, [selectedDate]);

	const dateStr = formatLocalDate(currentDate);

	const { timereports, statusState, refetch: refetchTimereports } = useGetTimereports(dateStr, dateStr);
	const { userInfo } = useGetUserInfo();
	const { activities } = useGetActivities();
	const { favorites } = useGetFavoriteActivities();
	const { toggleFavorite } = useSetFavoriteActivity();
	const { create, createAsync, statusState: createStatus } = useCreateTimereport();
	const { updateAsync } = useUpdateTimereport();
	const { submitAllAsync, resetSubmitAll, monthStats, savedHoursBefore, monthReportUpToDate } = useSubmitAllTimereports();
	const { types: timereportTypes } = useGetTimereportTypes();
	//const { updateFlagDate } = useUpdateFlag();
	const { isMobile } = useWindowSize();
	const { showModal, hideModal } = useGlobalState();
	const {
		settings: { monthlySubmitEnabled },
	} = useTimereportSettings();

	useEffect(() => {
		if (monthStats && monthStats.length > 0) {
			showModal(
				<MonthStatsReport
					stats={monthStats}
					savedHoursBefore={savedHoursBefore}
					upToDate={monthReportUpToDate}
					onClose={hideModal}
					onSave={() => hideModal()}
				/>,
				t('TIMEREPORT_USER.DAY_VIEW.MODAL.MONTH_REPORT_TITLE')
			);
			resetSubmitAll();
		}
	}, [hideModal, monthReportUpToDate, monthStats, resetSubmitAll, savedHoursBefore, showModal, t]);

	const [isSaving, setIsSaving] = useState(false);
	const savingRef = useRef(false);
	const [actionError, setActionError] = useState<string | null>(null);

	const flagDate = userInfo?.dateTimeReportFlag?.split('T')[0] ?? null;
	const employmentDate = userInfo?.dateEmployment?.split('T')[0] ?? null;
	const isBeforeEmployment = employmentDate != null && dateStr < employmentDate;
	const isLocked = isBeforeEmployment || (flagDate != null && dateStr <= flagDate);

	const today = new Date();
	const previousMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
	const previousMonthEndStr = formatLocalDate(previousMonthEnd);
	const selectedMonthEnd = getLastDayOfMonth(currentDate);
	const previousMonthOutstanding =
		!!userInfo && (!flagDate || flagDate < previousMonthEndStr) && (!employmentDate || employmentDate <= previousMonthEndStr);
	const showPreviousMonth =
		monthlySubmitEnabled &&
		previousMonthOutstanding &&
		(isLocked || formatLocalDate(selectedMonthEnd) !== previousMonthEndStr);
	const monthLabel = (date: Date) => date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
	const fullDateLabel = (date: Date) => date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
	const submitLabel = (date: Date) => t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.SUBMIT', { month: monthLabel(date) });

	const dayItems = useMemo(() => timereports?.filter((t) => t.date.startsWith(dateStr)) ?? [], [timereports, dateStr]);

	// Build rows: one per favorite activity, plus any non-favorite entries
	// In SHOW_ALL_ACTIVITIES mode, all activities are shown as rows regardless of favorites
	const rows = useMemo(() => {
		const result: { key: string; activity: TimereportActivity | null; item: TimereportItem | null }[] = [];
		const matchedItemIds = new Set<number>();

		if (SHOW_ALL_ACTIVITIES && activities) {
			// Show all available activities as rows
			for (const act of activities) {
				const actItems = dayItems.filter((t) => t.projectId === act.projectId && t.activityId === act.id);

				if (actItems.length === 0) {
					result.push({ key: `${act.projectId}-${act.id}`, activity: act, item: null });
				} else {
					for (const item of actItems) {
						matchedItemIds.add(item.id);
						result.push({
							key: `${act.projectId}-${act.id}-${item.id}`,
							activity: act,
							item,
						});
					}
				}
			}

			// Add timereport entries that don't match any known activity (e.g. vacation, parental leave)
			for (const item of dayItems) {
				if (!matchedItemIds.has(item.id)) {
					result.push({
						key: `orphan-${item.id}`,
						activity: null,
						item,
					});
				}
			}
		} else {
			// Original behavior: favorites + existing entries
			if (favorites) {
				for (const fav of favorites) {
					const favItems = dayItems.filter((t) => t.projectId === fav.projectId && t.activityId === fav.id);

					if (favItems.length === 0) {
						result.push({ key: `${fav.projectId}-${fav.id}`, activity: fav, item: null });
					} else {
						for (const item of favItems) {
							matchedItemIds.add(item.id);
							result.push({
								key: `${fav.projectId}-${fav.id}-${item.id}`,
								activity: fav,
								item,
							});
						}
					}
				}
			}

			// Add non-favorite entries that exist for this day (including those without matching activity)
			for (const item of dayItems) {
				if (!matchedItemIds.has(item.id)) {
					const act = activities?.find((a) => a.projectId === item.projectId && a.id === item.activityId) ?? null;
					result.push({ key: `orphan-${item.id}`, activity: act, item });
				}
			}
		}

		return result;
	}, [favorites, dayItems, activities]);

	// Track editable state per row (keyed by `projectId-activityId`)
	const failedDrafts = useRef<{ date: string; rows: Record<string, RowState> }>({ date: dateStr, rows: {} });
	const [rowStates, setRowStates] = useState<Record<string, RowState>>({});

	// Sync row states when rows or date change
	useEffect(() => {
		if (savingRef.current) return;
		const newStates: Record<string, RowState> = {};
		for (const row of rows) {
			newStates[row.key] = (failedDrafts.current.date === dateStr && failedDrafts.current.rows[row.key]) || {
				hours: row.item?.units ?? 0,
				comment: row.item?.comment ?? '',
			};
		}
		setRowStates(newStates);
	}, [rows, dateStr, isSaving]);

	const updateRowState = (key: string, field: keyof RowState, value: string | number) => {
		setRowStates((prev) => ({
			...prev,
			[key]: { ...prev[key], [field]: value },
		}));
	};

	// Add-new-entry state
	type ActivityOption = { Id: number; Name: string; activity: TimereportActivity };
	const [addActivity, setAddActivity] = useState<ActivityOption | null>(null);
	const [addTypeId, setAddTypeId] = useState<number>(0);
	const [addHours, setAddHours] = useState(0);
	const [addComment, setAddComment] = useState('');

	// Build dropdown options: all activities formatted as "customer → project → activity"
	const activityOptions: ActivityOption[] = useMemo(() => {
		if (!activities) return [];
		return activities.map((a) => ({
			Id: a.id,
			Name: `${a.customer || '—'} › ${a.projectName} › ${a.name}`,
			activity: a,
		}));
	}, [activities]);

	// Selected activity's types for the type DDL
	const addActivityTypes = useMemo(() => {
		if (!addActivity) return [];
		return addActivity.activity.timereportTypes.map((t) => ({
			id: t.timereportTypeId,
			name: timereportTypes?.find((tt) => tt.id === t.timereportTypeId)?.name ?? `Type ${t.timereportTypeId}`,
		}));
	}, [addActivity, timereportTypes]);

	// Auto-select type when activity changes
	useEffect(() => {
		if (addActivity && addActivity.activity.timereportTypes.length > 0) {
			setAddTypeId(addActivity.activity.timereportTypes[0].timereportTypeId);
		} else {
			setAddTypeId(0);
		}
	}, [addActivity]);

	const handleAdd = () => {
		if (!addActivity || addHours <= 0) return;
		const act = addActivity.activity;
		const typeId = addActivityTypes.length > 1 ? addTypeId : (act.timereportTypes[0]?.timereportTypeId ?? 0);
		create({
			projectId: act.projectId,
			activityId: act.id,
			typeId,
			objectType: null,
			objectId: null,
			hours: addHours,
			date: dateStr,
			comment: addComment,
		});
		setAddActivity(null);
		setAddTypeId(0);
		setAddHours(0);
		setAddComment('');
	};

	const saveRows = async () => {
		if (isLocked) return;
		const saves: Promise<unknown>[] = [];
		failedDrafts.current = { date: dateStr, rows: {} };
		const retainOnFailure = (key: string, request: Promise<unknown>) =>
			request.catch((error: unknown) => {
				failedDrafts.current.rows[key] = rowStates[key];
				throw error;
			});
		for (const row of rows) {
			const state = rowStates[row.key];
			if (!state || row.item?.isSubmitted || row.item?.isAttested) continue;

			const defaultTypeId =
				row.activity && row.activity.timereportTypes.length > 0 ? row.activity.timereportTypes[0].timereportTypeId : 0;

			if (row.item) {
				// Update existing if changed
				if (state.hours !== row.item.units || state.comment !== row.item.comment) {
					saves.push(
						retainOnFailure(
							row.key,
							updateAsync({
								id: row.item.id,
								projectId: row.item.projectId,
								activityId: row.item.activityId,
								typeId: row.item.typeId,
								objectType: row.item.objectType,
								objectId: row.item.objectId,
								hours: state.hours,
								date: dateStr,
								comment: state.comment,
							})
						)
					);
				}
			} else if (state.hours > 0 && row.activity) {
				// Create new entry for activity with hours filled in (only if activity is known)
				saves.push(
					retainOnFailure(
						row.key,
						createAsync({
							projectId: row.activity.projectId,
							activityId: row.activity.id,
							typeId: defaultTypeId,
							objectType: null,
							objectId: null,
							hours: state.hours,
							date: dateStr,
							comment: state.comment,
						})
					)
				);
			}
		}
		const results = await Promise.allSettled(saves);
		if (results.some((result) => result.status === 'rejected')) throw new Error('Save failed');
	};

	const saveAndSubmit = async (endDate?: string) => {
		if (savingRef.current) return;
		savingRef.current = true;
		setIsSaving(true);
		setActionError(null);
		let saved = false;
		try {
			await saveRows();
			saved = true;
			if (endDate) await submitAllAsync(endDate);
		} catch {
			setActionError(t(`TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.${saved ? 'SUBMIT_ERROR' : 'SAVE_ERROR'}`));
		} finally {
			// Refresh IDs for successful creates before allowing a retry after partial failure.
			await refetchTimereports();
			savingRef.current = false;
			setIsSaving(false);
		}
	};

	const confirmMonth = (target: Date) => {
		const endDate = formatLocalDate(target);
		const isCurrentOrFuture = endDate >= formatLocalDate(getLastDayOfMonth(today));
		const submit = (date: string) => {
			hideModal();
			void saveAndSubmit(date);
		};
		showModal(
			<div className='biz-timereport-month-confirm'>
				{isCurrentOrFuture && (
					<div className='biz-timereport-month-confirm__warning' role='alert'>
						<strong>{t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.WARNING', { month: monthLabel(target) })}</strong>
						<p>{t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.WARNING_DETAIL', { date: fullDateLabel(target) })}</p>
					</div>
				)}
				<p>{t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.MESSAGE', { date: fullDateLabel(target) })}</p>
				<p>{t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.SAVE_NOTE', { date: fullDateLabel(currentDate) })}</p>
				{isCurrentOrFuture && previousMonthOutstanding && (
					<p>
						{t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.ALTERNATIVE_NOTE', {
							date: fullDateLabel(previousMonthEnd),
						})}
					</p>
				)}
				<div className='biz-timereport-month-confirm__actions'>
					<BizButton text={t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.CANCEL')} onClick={hideModal} legacy={false} />
					<BizButton
						text={
							isCurrentOrFuture
								? t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.ANYWAY', { month: monthLabel(target) })
								: submitLabel(target)
						}
						onClick={() => submit(endDate)}
						legacy={false}
						isLead={!isCurrentOrFuture}
					/>
					{isCurrentOrFuture && previousMonthOutstanding && (
						<BizButton
							text={t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.INSTEAD', { month: monthLabel(previousMonthEnd) })}
							onClick={() => submit(previousMonthEndStr)}
							legacy={false}
							isLead={true}
						/>
					)}
				</div>
			</div>,
			t('TIMEREPORT_USER.DAY_VIEW.MONTH_SUBMISSION.TITLE', { date: fullDateLabel(target) })
		);
	};

	const handleToggleFavorite = (activity: TimereportActivity) => {
		toggleFavorite(activity.projectId, activity.id, !activity.isFavorite);
	};

	const handlePrevDay = () => setCurrentDate((prev) => addDays(prev, -1));
	const handleNextDay = () => setCurrentDate((prev) => addDays(prev, 1));
	// hide for now because it confuses users.
	// const handleToday = () => setCurrentDate(new Date());

	const localizedDateLabel = currentDate.toLocaleDateString(locale, {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
	});

	const onSaveClick = (key: string) => {
		const endDate =
			key === 'submit' ? dateStr : key === 'submitweek' ? formatLocalDate(addDays(getMonday(currentDate), 6)) : undefined;
		void saveAndSubmit(endDate);
	};

	return (
		<div className='biz-timereport-day-view'>
			{/*<MonthStatsReport stats={monthStatsTestDate} onClose={() => console.log('close')} />*/}
			<div className='biz-timereport-day-view__header'>
				<BizButton text='◀' onClick={handlePrevDay} disabled={isSaving} legacy={false} />
				{/* <BizButton text={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.TODAY')} onClick={handleToday} /> */}
				<span className='biz-timereport-day-view__date-label'>{localizedDateLabel}</span>
				<BizButton text='▶' onClick={handleNextDay} disabled={isSaving} legacy={false} />
			</div>

			{statusState === 'LOADING' && <p>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.STATE.LOADING')}</p>}

			{isLocked && (
				<div className='biz-timereport-day-view__locked-info'>
					{isBeforeEmployment
						? t('TIMEREPORT_USER.DAY_VIEW.LOCKED.BEFORE_EMPLOYMENT', { date: employmentDate })
						: t('TIMEREPORT_USER.DAY_VIEW.LOCKED.SUBMITTED_THROUGH', { date: flagDate })}
				</div>
			)}

			{!isLocked && !SHOW_ALL_ACTIVITIES && (
				<div className='biz-timereport-day-view__add-row'>
					<div className='biz-timereport-day-view__add-row-select'>
						<BizAutoComplete
							options={activityOptions}
							value={addActivity ?? undefined}
							onChange={(opt) => setAddActivity(opt)}
							componentLabel={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.SEARCH_ACTIVITY')}
						/>
					</div>
					{addActivityTypes.length > 1 && (
						<div className='biz-timereport-day-view__add-row-type'>
							<BizSelect
								options={addActivityTypes}
								selectedId={addTypeId}
								onChange={(opt) => setAddTypeId((opt?.id as number) ?? 0)}
								noSelectText={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.SELECT_TYPE')}
							/>
						</div>
					)}
					<div className='biz-timereport-day-view__add-row-hours'>
						<BizInput
							type='number'
							value={String(addHours || '')}
							onChange={(e) => setAddHours(parseFloat(e.target.value) || 0)}
							placeholder={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.HOURS')}
							min='0'
							max='24'
						/>
					</div>
					<div className='biz-timereport-day-view__add-row-comment'>
						<BizTextArea
							value={addComment}
							onChange={(e) => setAddComment(e.target.value)}
							placeholder={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.COMMENT')}
							rows={1}
						/>
					</div>
					<BizButton
						text={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.ADD')}
						onClick={handleAdd}
						isLead={true}
						legacy={false}
						icon='fa-solid fa-plus'
						state={createStatus}
						disabled={!addActivity || addHours <= 0}
						size='SMALL'
					/>
				</div>
			)}

			{/* Favorites / entries table */}
			<div className='biz-timereport-day-view__table'>
				<div className='biz-timereport-day-view__table-header'>
					<div className='biz-timereport-day-view__col-fav'></div>
					<div className='biz-timereport-day-view__col-customer'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.CUSTOMER')}
					</div>
					<div className='biz-timereport-day-view__col-project'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.PROJECT')}
					</div>
					<div className='biz-timereport-day-view__col-activity'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.ACTIVITY')}
					</div>
					<div className='biz-timereport-day-view__col-hours'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.HOURS')}
					</div>
					<div className='biz-timereport-day-view__col-comment'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.COMMENT')}
					</div>
				</div>

				{rows.map((row) => {
					const state = rowStates[row.key] ?? { hours: 0, comment: '' };
					const isSubmitted = row.item?.isSubmitted ?? false;
					const isAttested = row.item?.isAttested ?? false;
					const rowDisabled = isSaving || isLocked || isSubmitted || isAttested;

					return (
						<div
							key={row.key}
							className={`biz-timereport-day-view__table-row${rowDisabled ? ' biz-timereport-day-view__table-row--disabled' : ''}`}
						>
							<div className='biz-timereport-day-view__row-info'>
								<div className='biz-timereport-day-view__col-fav'>
									{!SHOW_ALL_ACTIVITIES && row.activity && (
										<span
											className='biz-timereport-day-view__fav-toggle'
											onClick={() => handleToggleFavorite(row.activity!)}
											title={
												row.activity.isFavorite
													? t('TIMEREPORT_USER.REPORT_VIEW.SHARED.FAVORITES.REMOVE')
													: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.FAVORITES.ADD')
											}
										>
											{row.activity.isFavorite ? '★' : '☆'}
										</span>
									)}
								</div>
								<div className='biz-timereport-day-view__col-customer'>
									{row.activity?.customer || row.item?.customerName || '—'}
								</div>
								<div className='biz-timereport-day-view__col-project'>
									{row.activity?.projectName || row.item?.projectName || '—'}
								</div>
								<div className='biz-timereport-day-view__col-activity'>
									{row.activity?.name || row.item?.activityName || '—'}
								</div>
								<div className='biz-timereport-day-view__col-activity-mobile'>
									{row.activity?.customer || row.item?.customerName || '—'} →{' '}
									{row.activity?.projectName || row.item?.projectName || '—'} →{' '}
									{row.activity?.name || row.item?.activityName || '—'}
								</div>
							</div>
							<div className='biz-timereport-day-view__row-inputs'>
								<div className='biz-timereport-day-view__col-hours'>
									<BizInput
										type='number'
										value={String(state.hours || '')}
										onChange={(e) => updateRowState(row.key, 'hours', parseFloat(e.target.value) || 0)}
										min='0'
										max='24'
										placeholder={
											isMobile ? t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.HOURS') : undefined
										}
										readonly={rowDisabled}
									/>
								</div>
								<div className='biz-timereport-day-view__col-comment'>
									<BizTextArea
										value={state.comment}
										onChange={(e) => updateRowState(row.key, 'comment', e.target.value)}
										placeholder={
											isMobile
												? t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.COMMENT_MOBILE')
												: undefined
										}
										readonly={rowDisabled}
										rows={1}
									/>
								</div>
							</div>
						</div>
					);
				})}

				{rows.length === 0 && (
					<div className='biz-timereport-day-view__empty'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.EMPTY.NO_FAVORITES')}
					</div>
				)}
			</div>

			{actionError && (
				<p role='alert' className='biz-timereport-day-view__error'>
					{actionError}
				</p>
			)}
			{(!isLocked || showPreviousMonth) && (
				<div className='biz-timereport-day-view__actions'>
					{monthlySubmitEnabled ? (
						<>
							{showPreviousMonth && (
								<BizButton
									text={submitLabel(previousMonthEnd)}
									onClick={() => confirmMonth(previousMonthEnd)}
									legacy={false}
									isLead={true}
									disabled={isSaving || statusState !== 'SUCCESS'}
								/>
							)}
							{!isLocked && (
								<>
									<BizButton
										text={submitLabel(selectedMonthEnd)}
										onClick={() => confirmMonth(selectedMonthEnd)}
										legacy={false}
										disabled={isSaving || !userInfo || statusState !== 'SUCCESS'}
									/>
									<BizButton
										text={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SAVE')}
										onClick={() => void saveAndSubmit()}
										legacy={false}
										isLead={true}
										disabled={!userInfo || statusState !== 'SUCCESS'}
										state={isSaving ? 'LOADING' : 'INITIAL'}
									/>
								</>
							)}
						</>
					) : (
						<BizSplitButton
							options={[
								{
									Key: 'save',
									Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SAVE'),
								},
								{
									Key: 'submit',
									Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SUBMIT_REPORTS'),
								},
								{
									Key: 'submitweek',
									Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SUBMIT_AND_CLOSE_WEEK'),
								},
							]}
							handleClick={onSaveClick}
							isLead={true}
							noShadow={true}
							size='SMALL'
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default TimereportDayView;
